import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import { TenantUser } from '@/common/models';
import { UserDetailsEditUserModal, UserDetailsDeleteUserModal } from '@/components/userDetails';
import { FormatHelper, PaginationHelper } from '@/common/helpers';
import { UserDetailsFormDataUserEdit } from '@/common/viewModels/userDetails/form/data/UserDetailsFormDataUserEdit';
import { UserDetailsFormStateUserEdit } from '@/common/viewModels/userDetails/form/state/UserDetailsFormStateUserEdit';
import { UsersHelper } from '@/common/helpers/UsersHelper';
import { TelstraUIVueSvg, Pagination } from '@/external/telstra-ui-vue/components';

@Component({
  components: {
    UserDetailsEditUserModal,
    UserDetailsDeleteUserModal,
    TelstraUIVueSvg,
    Pagination,
  },
})

export default class UsersList extends Vue {
  readonly pageSizeOptions: string[] = ['50', '100', '200'];

  @Prop({ required: true }) users!: TenantUser[];
  @Prop({ required: true }) filteredUsers!: TenantUser[];
  @Prop({ required: true }) currentPage!: number;
  @Prop({ required: true }) tenantUserEditFormData!: UserDetailsFormDataUserEdit;
  @Prop({ required: true }) tenantUserEditFormState!: UserDetailsFormStateUserEdit;
  @Prop({ required: true }) user!: TenantUser;
  @Prop({ required: true }) tenantUserEditError!: string;
  @Prop({ required: true }) tenantUserDeleteError!: string;
  @Prop({ required: true }) isLoading!: boolean;
  @Prop({ default: true }) isEditUserFormValid!: boolean;
  @Prop({ required: true }) isSelfInviteEnabled!: boolean;
  
  pageSize: number = 50;

  get items(): any[] {
    return this.filteredUsers ? this.filteredUsers : [];
  }

  get headers(): any[] {
    if (!this.isSelfInviteEnabled) {
      return  [
        { text: 'User', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Status', value: 'status' },
        { text: 'Date invited', value: 'invitationTimeUtc' },
        { text: 'Last login', value: 'lastLogin' },
        { text: 'Actions', value: 'actions', sortable: false },
      ];
    } else {
      return  [
        { text: 'User', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Status', value: 'status' },
        { text: 'Date invited', value: 'invitationTimeUtc' },
        { text: 'Last login', value: 'lastLogin' },
      ];
    }
  }

  get isLastActiveUser(): boolean {
    return !(this.users.length > 1);
  }

  get pages(): number {
    return PaginationHelper.pages(this.items, this.pageSize);
  }

  get showPagination(): boolean {
    return PaginationHelper.showPagination(this.items, this.pageSizeOptions);
  }

  get canPrevious(): boolean {
    return PaginationHelper.canPrevious(this.currentPage);
  }

  get canNext(): boolean {
    return PaginationHelper.canNext(this.currentPage, this.pages);
  }

  pageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
  }

  @Emit()
  pageChange(pageNumber: number): void {
    this.$emit('pageChange', pageNumber);
  }

  @Emit()
  editUser(user: TenantUser) {
    this.$emit('getUser', user.id);
  }

  @Emit()
  deleteUser(user: TenantUser) {
    this.$emit('getDeleteUser', user);
  }

  @Emit()
  onEditUserFormInputChange(fieldName: string, value: any) {
    this.$emit('editUserFormInputChange', fieldName, value);
  }

  @Emit()
  onEditTenantUser(): void {
    this.$emit('editTenantUser');
  }

  @Emit()
  onDeleteTenantUser(): void {
    this.$emit('deleteTenantUser');
  }

  showEditUserModal(): void {
    (this.$refs.modalEditUser as UserDetailsEditUserModal).showModal();
  }

  showDeleteUserModal(): void {
    (this.$refs.modalDeleteUser as UserDetailsDeleteUserModal).showModal();
  }

  hideEditUserModal(): void {
    (this.$refs.modalEditUser as UserDetailsEditUserModal).hideModal();
  }

  hideDeleteUserModal(): void {
    (this.$refs.modalDeleteUser as UserDetailsDeleteUserModal).hideModal();
  }

  getBadgeColor(status: string): string {
    if (status === 'Active') {
      return 'success';
    } else if (status === 'Inactive') {
      return 'secondary';
    }
    return 'warning';
  }

  getBadgeOutlined(status: string): boolean {
    return (status === 'Inactive');
  }

  getUserDisplayName(user: TenantUser): string {
    return UsersHelper.getUserDisplayName(user);
  }

  formatDate(date: Date): string {
    return date ? FormatHelper.formatDate(date.toString()) : '';
  }

  @Emit()
  userClicked(item: TenantUser): void {
    this.$emit('userClicked', item.tenantId, item.id);
  }

}
